import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-work-order-form',
  templateUrl: './work-order-form.component.html',
  styleUrls: ['./work-order-form.component.css']
})
export class WorkOrderFormComponent implements OnInit {
  workOrderForm!: FormGroup;
  itemsFormArray!: FormArray;
  editMode = false;
  workOrderId: number | null = null;
  projects: any[] = [];
  clients: any[] = [];
  vendors: any[] = [];
  loading = false;
  submitted = false;
  projectData: any = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private toastrService: ToastrMsgService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      if (params['id'] && params['id'] !== 'new') {
        this.workOrderId = +params['id'];
        this.editMode = true;
        this.loadWorkOrder();
      }
    });

    this.loadProjects();
    this.loadClients();
    this.loadVendors();
    this.workOrderForm.get('vendor_id')?.valueChanges.subscribe(() => this.updateVendorValidators());
    this.updateVendorValidators();
  }

  initializeForm() {
    this.workOrderForm = this.fb.group({
      // Project Information
      project_id: ['', Validators.required],
      project_planning_id: [''],
      
      // Work Order Details
      work_order_number: [''],
      issue_date: [new Date().toISOString().split('T')[0], Validators.required],
      purchase_order_number: [''],
      
      // Client Information
      client_id: [''],
      client_name: ['', Validators.required],
      client_address: [''],
      client_cin: [''],
      client_pan: [''],
      client_tan: [''],
      client_gstn: [''],
      client_contact_name: [''],
      client_contact_phone: [''],
      client_contact_email: [''],
      bill_to_address: [''],
      ship_to_address: [''],
      
      // Vendor Information
      vendor_id: ['manual'],
      vendor_name: [''],
      vendor_address: [''],
      vendor_gstn: [''],
      vendor_pan: [''],
      vendor_contact_name: [''],
      vendor_contact_phone: [''],
      vendor_contact_email: [''],
      vendor_off_tel: [''],
      
      // Shipping
      ship_via: [''],
      cif: [''],
      
      // Tax Percentages
      cgst_percentage: [0],
      sgst_percentage: [0],
      igst_percentage: [18],
      tax1_percentage: [0],
      tax2_percentage: [0],
      tax3_percentage: [0],
      shipping_charges: [0],
      other_charges: [0],
      
      // Terms
      payment_details: [''],
      payment_terms: [''],
      notes: [''],
      special_instructions: [''],
      
      // Items
      items: this.fb.array([])
    });

    this.itemsFormArray = this.workOrderForm.get('items') as FormArray;
    this.addItem(); // Add one empty item by default
  }

  get items() {
    return this.workOrderForm.get('items') as FormArray;
  }

  addItem() {
    const itemForm = this.fb.group({
      item_code: [''],
      description: ['', Validators.required],
      quantity: [0, [Validators.required, Validators.min(0)]],
      unit: ['pcs', Validators.required],
      unit_price: [0, [Validators.required, Validators.min(0)]],
      specifications: [''],
      hsn_code: [''],
      sac_code: ['']
    });
    this.items.push(itemForm);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
  }

  onProjectChange() {
    const projectId = this.workOrderForm.get('project_id')?.value;
    if (projectId) {
      this.apiService.getProjectDataForWorkOrder({ project_id: projectId }).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.projectData = res.data;
            // Auto-fill client information
            if (res.data.project) {
              this.workOrderForm.patchValue({
                client_name: res.data.project.client_name || '',
                client_address: res.data.project.client_address || '',
                client_contact_phone: res.data.project.client_phone || '',
                client_contact_email: res.data.project.client_email || '',
                bill_to_address: res.data.project.client_address || '',
                ship_to_address: res.data.project.client_address || ''
              });
            }
          }
        },
        error: (err: any) => {
          console.error('Error loading project data:', err);
        }
      });
    }
  }

  loadProjects() {
    this.apiService.getProjectCommonAll({ limit: 1000 }).subscribe({
      next: (res: any) => {
        this.projects = res?.data || res || [];
      },
      error: (err: any) => {
        console.error('Error loading projects:', err);
      }
    });
  }

  loadClients() {
    this.apiService.getClients().subscribe({
      next: (res: any) => {
        this.clients = res?.data || [];
      },
      error: (err: any) => {
        console.error('Error loading clients:', err);
      }
    });
  }

  loadVendors() {
    this.apiService.listVendorAccounts().subscribe({
      next: (res: any) => {
        this.vendors = res?.data || res?.vendors || [];
      },
      error: (err: any) => {
        console.error('Error loading vendors:', err);
      }
    });
  }

  get isVendorFromList(): boolean {
    const vendorId = this.workOrderForm.get('vendor_id')?.value;
    return Boolean(vendorId && vendorId !== 'manual');
  }

  showFieldError(controlName: string): boolean {
    const control = this.workOrderForm.get(controlName);
    return Boolean(control && control.invalid && (control.touched || this.submitted));
  }

  showItemError(index: number, controlName: string): boolean {
    const control = this.items.at(index).get(controlName);
    return Boolean(control && control.invalid && (control.touched || this.submitted));
  }

  updateVendorValidators(): void {
    const vendorName = this.workOrderForm.get('vendor_name');
    if (!vendorName) return;

    if (this.isVendorFromList) {
      vendorName.clearValidators();
    } else {
      vendorName.setValidators([Validators.required]);
    }
    vendorName.updateValueAndValidity({ emitEvent: false });
  }

  onVendorSelectionChange(vendorId: string | number): void {
    if (!vendorId || vendorId === 'manual') {
      this.workOrderForm.patchValue({
        vendor_name: '',
        vendor_address: '',
        vendor_gstn: '',
        vendor_pan: '',
        vendor_contact_name: '',
        vendor_contact_phone: '',
        vendor_contact_email: '',
        vendor_off_tel: ''
      });
      return;
    }
    this.onVendorChange(Number(vendorId));
  }

  onClientChange(clientId: number | string): void {
    if (!clientId) return;

    const client = this.clients.find((c: any) => c.client_id === clientId);
    if (!client) return;

    const address = this.buildClientAddress(client);
    const clientName = client.client_name
      || `${client.client_first_name || ''} ${client.client_last_name || ''}`.trim();

    this.workOrderForm.patchValue({
      client_id: client.client_id,
      client_name: clientName,
      client_address: address,
      client_contact_name: client.contact_person || '',
      client_contact_phone: client.contact_number || '',
      bill_to_address: address,
      ship_to_address: address
    });
  }

  copyBillToShip(): void {
    const billTo = this.workOrderForm.get('bill_to_address')?.value || '';
    this.workOrderForm.patchValue({ ship_to_address: billTo });
  }

  private buildClientAddress(client: any): string {
    if (client.address) return client.address;
    return [
      client.line_1,
      client.line_2,
      client.villages,
      client.sub_district,
      client.district,
      client.state
    ].filter(Boolean).join(', ');
  }

  onVendorChange(vendorId: number) {
    const vendor = this.vendors.find((v: any) => v.id === vendorId || v.vendor_id === vendorId);
    if (!vendor) return;
    this.workOrderForm.patchValue({
      vendor_id: vendor.id || vendor.vendor_id,
      vendor_name: vendor.company_name || vendor.vendor_name || vendor.name || '',
      vendor_address: vendor.address || vendor.registered_address || '',
      vendor_gstn: vendor.gstn || vendor.gst_number || '',
      vendor_pan: vendor.pan || vendor.pan_number || '',
      vendor_contact_name: vendor.contact_name || vendor.primary_contact || '',
      vendor_contact_phone: vendor.phone || vendor.contact_phone || '',
      vendor_contact_email: vendor.email || vendor.contact_email || '',
      vendor_off_tel: vendor.office_phone || vendor.off_tel || ''
    });
  }

  loadWorkOrder() {
    if (!this.workOrderId) return;

    this.loading = true;
    this.apiService.getWorkOrderById(this.workOrderId).subscribe({
      next: (res: any) => {
        if (res.success) {
          const workOrder = res.data;
          
          // Clear existing items
          while (this.items.length !== 0) {
            this.items.removeAt(0);
          }

          // Patch form values
          this.workOrderForm.patchValue({
            project_id: workOrder.project_id,
            project_planning_id: workOrder.project_planning_id,
            work_order_number: workOrder.work_order_number,
            issue_date: workOrder.issue_date,
            purchase_order_number: workOrder.purchase_order_number,
            client_id: workOrder.client_id,
            client_name: workOrder.client_name,
            client_address: workOrder.client_address,
            client_cin: workOrder.client_cin,
            client_pan: workOrder.client_pan,
            client_tan: workOrder.client_tan,
            client_gstn: workOrder.client_gstn,
            client_contact_name: workOrder.client_contact_name,
            client_contact_phone: workOrder.client_contact_phone,
            client_contact_email: workOrder.client_contact_email,
            bill_to_address: workOrder.bill_to_address,
            ship_to_address: workOrder.ship_to_address,
            vendor_id: workOrder.vendor_id || 'manual',
            vendor_name: workOrder.vendor_name,
            vendor_address: workOrder.vendor_address,
            vendor_gstn: workOrder.vendor_gstn,
            vendor_pan: workOrder.vendor_pan,
            vendor_contact_name: workOrder.vendor_contact_name,
            vendor_contact_phone: workOrder.vendor_contact_phone,
            vendor_contact_email: workOrder.vendor_contact_email,
            vendor_off_tel: workOrder.vendor_off_tel,
            ship_via: workOrder.ship_via,
            cif: workOrder.cif,
            cgst_percentage: workOrder.cgst_percentage,
            sgst_percentage: workOrder.sgst_percentage,
            igst_percentage: workOrder.igst_percentage,
            tax1_percentage: workOrder.tax1_percentage,
            tax2_percentage: workOrder.tax2_percentage,
            tax3_percentage: workOrder.tax3_percentage,
            shipping_charges: workOrder.shipping_charges,
            other_charges: workOrder.other_charges,
            payment_details: workOrder.payment_details,
            payment_terms: workOrder.payment_terms,
            notes: workOrder.notes,
            special_instructions: workOrder.special_instructions
          });

          // Add items
          if (workOrder.items && workOrder.items.length > 0) {
            workOrder.items.forEach((item: any) => {
              const itemForm = this.fb.group({
                item_code: [item.item_code || ''],
                description: [item.description, Validators.required],
                quantity: [item.quantity, [Validators.required, Validators.min(0)]],
                unit: [item.unit, Validators.required],
                unit_price: [item.unit_price, [Validators.required, Validators.min(0)]],
                specifications: [item.specifications || ''],
                hsn_code: [item.hsn_code || ''],
                sac_code: [item.sac_code || '']
              });
              this.items.push(itemForm);
            });
          } else {
            this.addItem();
          }
          this.updateVendorValidators();
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading work order:', err);
        this.toastrService.error('Failed to load work order');
        this.loading = false;
      }
    });
  }

  onSubmit() {
    this.submitted = true;
    this.updateVendorValidators();

    if (this.workOrderForm.invalid) {
      this.toastrService.error('Please fill all required fields');
      this.workOrderForm.markAllAsTouched();
      return;
    }

    const formData = { ...this.workOrderForm.value };
    if (formData.vendor_id === 'manual') {
      formData.vendor_id = null;
    }
    if (!formData.client_id) {
      formData.client_id = null;
    }

    const observable = this.editMode
      ? this.apiService.updateWorkOrder(this.workOrderId!, formData)
      : this.apiService.createWorkOrder(formData);

    this.loading = true;
    observable.subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success(`Work order ${this.editMode ? 'updated' : 'created'} successfully`);
          this.router.navigate(['/home/work-orders', res.data.work_order_id || this.workOrderId]);
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error saving work order:', err);
        this.toastrService.error(err.error?.error || `Failed to ${this.editMode ? 'update' : 'create'} work order`);
        this.loading = false;
      }
    });
  }

  calculateItemAmount(index: number) {
    const item = this.items.at(index);
    const quantity = Number(item.get('quantity')?.value) || 0;
    const unitPrice = Number(item.get('unit_price')?.value) || 0;
    return this.roundAmount(quantity * unitPrice);
  }

  get subtotal(): number {
    return this.roundAmount(
      this.items.controls.reduce((sum, item) => {
        const quantity = Number(item.get('quantity')?.value) || 0;
        const unitPrice = Number(item.get('unit_price')?.value) || 0;
        return sum + quantity * unitPrice;
      }, 0)
    );
  }

  get cgstAmount(): number {
    return this.calculateTaxAmount('cgst_percentage');
  }

  get sgstAmount(): number {
    return this.calculateTaxAmount('sgst_percentage');
  }

  get igstAmount(): number {
    return this.calculateTaxAmount('igst_percentage');
  }

  get tdsAmount(): number {
    return this.calculateTaxAmount('tax1_percentage');
  }

  get ugstAmount(): number {
    return this.calculateTaxAmount('tax2_percentage');
  }

  get cessAmount(): number {
    return this.calculateTaxAmount('tax3_percentage');
  }

  get shippingAmount(): number {
    return Number(this.workOrderForm.get('shipping_charges')?.value) || 0;
  }

  get otherChargesAmount(): number {
    return Number(this.workOrderForm.get('other_charges')?.value) || 0;
  }

  get grandTotal(): number {
    return this.roundAmount(
      this.subtotal
      + this.cgstAmount
      + this.sgstAmount
      + this.igstAmount
      + this.tdsAmount
      + this.ugstAmount
      + this.cessAmount
      + this.shippingAmount
      + this.otherChargesAmount
    );
  }

  private calculateTaxAmount(controlName: string): number {
    const percentage = Number(this.workOrderForm.get(controlName)?.value) || 0;
    return this.roundAmount(this.subtotal * percentage / 100);
  }

  private roundAmount(amount: number): number {
    return Math.round(amount * 100) / 100;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  goBack() {
    this.router.navigate(['/home/work-orders']);
  }
}

